import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { Button } from "reactstrap";
import Column from "../Trading/Column";
import style from "./Trade.module.scss";

const Trading = forwardRef(
  (
    {
      tradings,
      counterparties,
      tradeRoutes,
      fixedSide = "buy",
      onChange,
      price,
      loading = false,
      sidesAux,
      maxDecimals,
    },
    ref
  ) => {
    useEffect(() => {
      onChange({
        sides: {
          buy: sidesAux.buy.map((b) => ({ ...b, price })),
          sell: sidesAux.sell.map((b) => ({ ...b, price })),
        },
      });
    }, [price]);

    useImperativeHandle(ref, () => ({
      validate,
      values,
    }));

    const handleNew = (side) => {
      const newId = Math.random().toString(36).substr(2, 4);

      onChange({
        sides: {
          ...sidesAux,
          [side]: [
            ...sidesAux[side],
            {
              id: newId,
              price,
              route: null,
              date: new Date().toDatetimeLocal(),
            },
          ],
        },
      });
    };

    const handleDel = (side, id) => {
      onChange({
        sides: {
          ...sidesAux,
          [side]: sidesAux[side].filter((r) => r.id != id),
        },
      });
    };

    const handleChange = (side, id, props) => {
      let changes = {};
      if (fixedSide == side && props["quantity"]) {
        changes.quantity = props.quantity;
      }

      changes.sides = {
        ...sidesAux,
        [side]: sidesAux[side].map((row) => {
          if (row.id == id) {
            const errors = row.errors
              ? Object.keys(props).reduce((obj, e) => {
                  obj[e] = undefined;
                  return obj;
                }, {})
              : {};
            return { ...row, ...props, errors };
          }
          return row;
        }),
      };

      onChange(changes);
    };

    const values = () => {
      const { buy, sell } = sidesAux;

      return [
        ...buy.map((b) => ({
          Side: "Buy",
          Quantity: +b.quantity,
          Price: +b.price,
          CounterPartyId: b.counterpartyId,
          CounterPartyName: b.counterpartyName,
          TradingRouteId: b.route,
          DateTime: b.date,
        })),
        ...sell.map((b) => ({
          Side: "Buy",
          Quantity: +b.quantity,
          Price: +b.price,
          CounterPartyId: b.counterpartyId,
          CounterPartyName: b.counterpartyName,
          TradingRouteId: b.route,
          DateTime: b.date,
        })),
      ];
    };

    const validate = () => {
      let hasErrors = false;
      const changedSides = { ...sidesAux };

      let fixedDate = new Date();
      if (changedSides[fixedSide].length > 0) {
        const fixedItem = changedSides[fixedSide][0];
        fixedDate = new Date(fixedItem.date);
        fixedDate = new Date(`${fixedDate.toDateLocal()}T00:00`);
      }

      const oppSide = fixedSide == "sell" ? "buy" : "sell";

      if (tradings.enabled) {
        changedSides[oppSide].forEach((e, i) => {
          let errors = {};

          if (e.counterpartyId === undefined || e.counterpartyId === null) {
            errors.counterpartyId = "Required field";
          }

          if (e.quantity === undefined || e.quantity === "" || e.quantity === 0) {
            errors.quantity = "Required field";
          }

          if (e.price === undefined || e.price === "" || e.price === 0) {
            errors.price = "Required field";
          }

          if (e.route === undefined || e.route == null) {
            errors.route = "Required field";
          }

          if (e.date === undefined || e.date === "") {
            errors.date = "Required field";
          } else {
            let itemDate = new Date(e.date);
            itemDate = new Date(`${itemDate.toDateLocal()}T00:00`);
            if (itemDate.getTime() != fixedDate.getTime()) {
              errors.date = "All dates should be equal";
            }
          }

          hasErrors |= Object.values(errors).length > 0;
          changedSides[oppSide][i].errors = errors;
        });

        if (
          changedSides[fixedSide][0].quantity === undefined ||
          changedSides[fixedSide][0].quantity === "" ||
          changedSides[fixedSide][0].quantity === 0
        ) {
          hasErrors = true;
          changedSides[fixedSide][0].errors = {
            quantity: "Required field",
          };
        }

        onChange(changedSides);

        // check same quantity
        if (!hasErrors) {
          const left = changedSides.buy.reduce((sum, e) => parseFloat(e.quantity) + sum, 0);
          const right = changedSides.sell.reduce((sum, e) => parseFloat(e.quantity) + sum, 0);

          if (left !== right) {
            return {
              error: true,
              global: "Buy side not match with Sell side.",
            };
          }
        }
      }

      return hasErrors ? { error: true } : {};
    };

    if (!tradings.enabled) {
      return (
        <div className={style.trading}>
          <Button size="sm" color="primary" className="mt-1 mb-1" onClick={() => onChange({ enabled: true })}>
            <i className="fa fa-plus" /> Trading
          </Button>
        </div>
      );
    }

    return (
      <React.Fragment>
        <h5 className={style.tradingTitle}>
          Trading
          <Button size="sm" color="primary" onClick={() => onChange({ enabled: false })}>
            <i className="fa fa-minus"></i> Discard trading
          </Button>
        </h5>
        <div className={style.trading}>
          <Column
            side={fixedSide == "buy" ? "buy" : "sell"}
            needFX={false}
            rows={fixedSide == "buy" ? sidesAux.buy : sidesAux.sell}
            loading={loading}
            newRow={handleNew}
            delRow={handleDel}
            onChange={handleChange}
            counterparties={counterparties}
            tradeRoutes={tradeRoutes}
            massiveMode={true}
            maxDecimals={maxDecimals}
          />
          <Column
            side={fixedSide == "buy" ? "sell" : "buy"}
            needFX={false}
            rows={fixedSide == "buy" ? sidesAux.sell : sidesAux.buy}
            loading={loading}
            newRow={handleNew}
            delRow={handleDel}
            onChange={handleChange}
            counterparties={counterparties}
            tradeRoutes={tradeRoutes}
            massiveMode={false}
            maxDecimals={maxDecimals}
          />
        </div>
      </React.Fragment>
    );
  }
);

export default Trading;
